import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  getDocument,
  GlobalWorkerOptions,
  type PDFDocumentLoadingTask,
  type PDFDocumentProxy,
} from 'pdfjs-dist'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import {
  pdfBookmarkStore,
  pdfMetadataStore,
  pdfStore,
  type PdfBookmarkPages,
  type PdfMetadata,
} from '../lib/blobStore'
import { sha256Hex } from '../lib/fileHash'
import { pageForSegno, pdfBookmarkKey, withSegnoPage, withoutSegnoPage } from '../lib/pdfBookmarks'
import { filenameFromPdfUrl, hasPdfSignature, MAX_LINKED_PDF_BYTES, parsePdfLink } from '../lib/pdfLink'
import type { Segno } from '../types'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'

GlobalWorkerOptions.workerSrc = workerUrl

export interface PdfViewerHandle {
  openFilePicker: () => void
  openSourceDialog: () => void
  removeActiveBookmark: () => void
}

interface Props {
  shareId: string
  segnos: Segno[]
  activeOrder: number | null
  onDocumentStateChange?: (hasDocument: boolean) => void
  onBookmarkStateChange?: (linked: boolean) => void
  onPreviousSegno?: () => void
  onNextSegno?: () => void
  previousSegnoDisabled?: boolean
  nextSegnoDisabled?: boolean
  playing?: boolean
  playbackDisabled?: boolean
  onTogglePlayback?: () => void
}

export const PdfViewer = forwardRef<PdfViewerHandle, Props>(function PdfViewer({
  shareId,
  segnos,
  activeOrder,
  onDocumentStateChange,
  onBookmarkStateChange,
  onPreviousSegno,
  onNextSegno,
  previousSegnoDisabled = false,
  nextSegnoDisabled = false,
  playing = false,
  playbackDisabled = false,
  onTogglePlayback,
}, ref) {
  const { t } = useI18n()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const loadingTaskRef = useRef<PDFDocumentLoadingTask | null>(null)
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null)
  const urlAbortRef = useRef<AbortController | null>(null)
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null)
  const [page, setPage] = useState(1)
  const [viewerWidth, setViewerWidth] = useState(0)
  const [fingerprint, setFingerprint] = useState('')
  const [bookmarks, setBookmarks] = useState<PdfBookmarkPages>({})
  const [message, setMessage] = useState('')
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkLoading, setLinkLoading] = useState(false)

  const activeSegno = useMemo(
    () => segnos.find((segno) => segno.order === activeOrder) ?? null,
    [activeOrder, segnos],
  )
  const activeBookmark = document
    ? pageForSegno(bookmarks, activeOrder, document.numPages)
    : null

  useImperativeHandle(ref, () => ({
    openFilePicker: () => fileInputRef.current?.click(),
    openSourceDialog: () => {
      setMessage('')
      setSourceDialogOpen(true)
    },
    removeActiveBookmark: () => {
      void removeActiveBookmark()
    },
  }))

  useEffect(() => {
    onDocumentStateChange?.(document != null)
  }, [document, onDocumentStateChange])

  useEffect(() => {
    onBookmarkStateChange?.(activeBookmark != null)
  }, [activeBookmark, onBookmarkStateChange])

  useEffect(() => {
    if (!sourceDialogOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !linkLoading) setSourceDialogOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [linkLoading, sourceDialogOpen])

  const loadBlob = useCallback(async (blob: Blob, metadata?: PdfMetadata | null): Promise<boolean> => {
    const resolvedFingerprint = metadata?.fingerprint || await sha256Hex(blob)
    const resolvedMetadata: PdfMetadata = {
      fingerprint: resolvedFingerprint,
      filename: metadata?.filename,
    }
    if (!metadata?.fingerprint) {
      await pdfMetadataStore.put(shareId, resolvedMetadata)
    }

    renderTaskRef.current?.cancel()
    const previousTask = loadingTaskRef.current
    loadingTaskRef.current = null
    setDocument(null)
    if (previousTask) {
      await previousTask.destroy().catch(() => undefined)
    }

    const loadingTask = getDocument({ data: await blob.arrayBuffer() })
    loadingTaskRef.current = loadingTask

    let pdf: PDFDocumentProxy
    try {
      pdf = await loadingTask.promise
    } catch {
      if (loadingTaskRef.current !== loadingTask) return false
      loadingTaskRef.current = null
      setMessage(t('pdf.error.openFile'))
      return false
    }

    if (loadingTaskRef.current !== loadingTask) {
      await loadingTask.destroy().catch(() => undefined)
      return false
    }

    const bookmarkKey = pdfBookmarkKey(shareId, resolvedFingerprint)
    const storedBookmarks = await pdfBookmarkStore.get(bookmarkKey)
    setDocument(pdf)
    setFingerprint(resolvedFingerprint)
    setBookmarks(storedBookmarks ?? {})
    setPage(1)
    setMessage('')
    return true
  }, [shareId])

  useEffect(() => {
    let cancelled = false
    void Promise.all([pdfStore.get(shareId), pdfMetadataStore.get(shareId)]).then(([blob, metadata]) => {
      if (!cancelled && blob) void loadBlob(blob, metadata)
    })
    return () => {
      cancelled = true
      renderTaskRef.current?.cancel()
      const loadingTask = loadingTaskRef.current
      loadingTaskRef.current = null
      if (loadingTask) void loadingTask.destroy().catch(() => undefined)
      urlAbortRef.current?.abort()
    }
  }, [loadBlob, shareId])

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const updateWidth = () => setViewerWidth(Math.max(0, Math.floor(wrap.clientWidth)))
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(wrap)
    return () => observer.disconnect()
  }, [document])

  useEffect(() => {
    if (!document || !canvasRef.current) return
    let cancelled = false
    renderTaskRef.current?.cancel()

    void document.getPage(page).then(async (pdfPage) => {
      if (cancelled || !canvasRef.current) return
      const base = pdfPage.getViewport({ scale: 1 })
      const targetWidth = Math.max(280, Math.min((viewerWidth || window.innerWidth * 0.92) - 20, 1600))
      const viewport = pdfPage.getViewport({ scale: targetWidth / base.width })
      const canvas = canvasRef.current
      const context = canvas.getContext('2d')
      if (!context) return

      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      canvas.style.width = `${Math.floor(viewport.width)}px`
      canvas.style.height = `${Math.floor(viewport.height)}px`

      const renderTask = pdfPage.render({ canvas, canvasContext: context, viewport })
      renderTaskRef.current = renderTask
      try {
        await renderTask.promise
      } catch (error) {
        if (!cancelled && !(error instanceof Error && error.name === 'RenderingCancelledException')) {
          setMessage(t('pdf.error.renderPage'))
        }
      }
    })

    return () => {
      cancelled = true
      renderTaskRef.current?.cancel()
    }
  }, [document, page, viewerWidth])

  useEffect(() => {
    if (!document) return
    const bookmarked = pageForSegno(bookmarks, activeOrder, document.numPages)
    if (bookmarked != null) setPage(bookmarked)
  }, [activeOrder, bookmarks, document])

  function openLocalFilePicker() {
    setMessage('')
    fileInputRef.current?.click()
  }

  async function selectPdf(file: File | undefined) {
    if (!file) return
    const filenameLooksLikePdf = file.name.toLowerCase().endsWith('.pdf')
    if (file.type && file.type !== 'application/pdf' && !filenameLooksLikePdf) {
      setMessage(t('pdf.error.pdfOnly'))
      setSourceDialogOpen(true)
      return
    }
    if (!(await hasPdfSignature(file))) {
      setMessage(t('pdf.error.notPdf'))
      setSourceDialogOpen(true)
      return
    }

    const metadata: PdfMetadata = {
      fingerprint: await sha256Hex(file),
      filename: file.name,
    }
    const loaded = await loadBlob(file, metadata)
    if (!loaded) {
      setSourceDialogOpen(true)
      return
    }
    await Promise.all([
      pdfStore.put(shareId, file),
      pdfMetadataStore.put(shareId, metadata),
    ])
    setSourceDialogOpen(false)
  }

  async function loadPdfLink(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    let resolvedUrl: URL
    try {
      resolvedUrl = parsePdfLink(linkUrl, window.location.protocol)
    } catch (error) {
      setMessage(userMessageForError(error, t('pdf.error.checkLink')))
      return
    }

    urlAbortRef.current?.abort()
    const controller = new AbortController()
    urlAbortRef.current = controller
    setLinkLoading(true)
    setMessage(t('pdf.message.readingLink'))

    try {
      const response = await fetch(resolvedUrl.toString(), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: controller.signal,
      })
      if (!response.ok) {
        throw new Error(`PDF_LINK_HTTP_${response.status}`)
      }
      const contentLength = Number(response.headers.get('content-length') ?? '0')
      if (Number.isFinite(contentLength) && contentLength > MAX_LINKED_PDF_BYTES) {
        throw new Error('PDF_LINK_TOO_LARGE')
      }

      const blob = await response.blob()
      if (blob.size === 0 || !(await hasPdfSignature(blob))) {
        throw new Error('PDF_LINK_NOT_PDF')
      }
      if (blob.size > MAX_LINKED_PDF_BYTES) {
        throw new Error('PDF_LINK_TOO_LARGE')
      }

      const metadata: PdfMetadata = {
        fingerprint: await sha256Hex(blob),
        filename: filenameFromPdfUrl(resolvedUrl),
      }
      const loaded = await loadBlob(blob, metadata)
      if (!loaded) return

      await Promise.all([
        pdfStore.put(shareId, blob),
        pdfMetadataStore.put(shareId, metadata),
      ])
      setLinkUrl(resolvedUrl.toString())
      setSourceDialogOpen(false)
      setMessage('')
    } catch (error) {
      if (controller.signal.aborted) return
      if (error instanceof TypeError) {
        setMessage(t('pdf.error.cors'))
      } else {
        setMessage(userMessageForError(error, t('pdf.error.readLink')))
      }
    } finally {
      if (urlAbortRef.current === controller) urlAbortRef.current = null
      setLinkLoading(false)
    }
  }

  async function saveActiveBookmark() {
    if (!document || !fingerprint || !activeSegno) return
    const next = withSegnoPage(bookmarks, activeSegno.order, page, document.numPages)
    await pdfBookmarkStore.put(pdfBookmarkKey(shareId, fingerprint), next)
    setBookmarks(next)
    setMessage(t('pdf.message.bookmarkLinked', { title: activeSegno.title, page }))
  }

  async function removeActiveBookmark() {
    if (!fingerprint || !activeSegno) return
    const next = withoutSegnoPage(bookmarks, activeSegno.order)
    await pdfBookmarkStore.put(pdfBookmarkKey(shareId, fingerprint), next)
    setBookmarks(next)
    setMessage(t('pdf.message.bookmarkRemoved', { title: activeSegno.title }))
  }

  function tapNavigate(event: React.MouseEvent<HTMLDivElement>) {
    if (!document) return
    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - rect.left) / rect.width
    if (ratio < 0.3) {
      setPage((value) => Math.max(1, value - 1))
    } else if (ratio > 0.7) {
      setPage((value) => Math.min(document.numPages, value + 1))
    }
  }

  return (
    <section className="pdf-panel">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          void selectPdf(file)
        }}
      />

      {sourceDialogOpen && (
        <div
          className="pdf-source-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !linkLoading) setSourceDialogOpen(false)
          }}
        >
          <section
            className="pdf-source-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pdf-source-dialog-title"
          >
            <div className="pdf-source-dialog-heading">
              <strong id="pdf-source-dialog-title">{t('pdf.dialog.title')}</strong>
              <button
                type="button"
                className="button ghost pdf-source-dialog-close"
                aria-label={t('pdf.dialog.close')}
                disabled={linkLoading}
                onClick={() => setSourceDialogOpen(false)}
              >
                ×
              </button>
            </div>

            <button
              type="button"
              className="button secondary pdf-local-file-button"
              disabled={linkLoading}
              onClick={openLocalFilePicker}
            >
              {t('pdf.dialog.chooseFile')}
            </button>

            <div className="pdf-source-divider"><span>{t('pdf.dialog.orLink')}</span></div>

            <form className="pdf-link-form" onSubmit={(event) => void loadPdfLink(event)}>
              <input
                type="url"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="https://example.com/score.pdf"
                aria-label={t('pdf.dialog.linkLabel')}
                value={linkUrl}
                disabled={linkLoading}
                onChange={(event) => setLinkUrl(event.target.value)}
              />
              <button type="submit" className="button primary" disabled={linkLoading}>
                {linkLoading ? t('pdf.dialog.loading') : t('pdf.dialog.openLink')}
              </button>
            </form>

            <p className="muted pdf-link-help">
              {t('pdf.dialog.linkHelp')}
            </p>
            {message && <p className="notice pdf-source-message" role="status">{message}</p>}
          </section>
        </div>
      )}

      <div className="pdf-page-toolbar">
        <div className="pdf-segno-toolbar-group" aria-label={t('pdf.toolbar.segnoNavigation')}>
          <button
            type="button"
            className="button ghost pdf-segno-nav-button"
            disabled={previousSegnoDisabled}
            aria-label={t('practice.segno.previous')}
            onClick={() => onPreviousSegno?.()}
          >
            ←
          </button>
          <span
            className={`pdf-active-segno-chip ${activeBookmark != null ? 'linked' : ''}`}
            title={activeSegno?.title ?? t('pdf.toolbar.noSegnoSelected')}
          >
            {activeSegno?.title ?? t('pdf.toolbar.noSegnoSelected')}
          </span>
          <button
            type="button"
            className="button ghost pdf-segno-nav-button"
            disabled={nextSegnoDisabled}
            aria-label={t('practice.segno.next')}
            onClick={() => onNextSegno?.()}
          >
            →
          </button>
          <button
            type="button"
            className={`button pdf-toolbar-play-button ${playing ? 'is-playing' : ''}`}
            disabled={playbackDisabled}
            aria-label={playing ? t('pdf.toolbar.stopLoop') : t('pdf.toolbar.playCurrent')}
            title={playing ? t('pdf.toolbar.stopLoop') : t('pdf.toolbar.playCurrent')}
            onClick={() => onTogglePlayback?.()}
          >
            <span aria-hidden="true">{playing ? '■' : '▶'}</span>
          </button>
        </div>

        <div className="pdf-page-link-group" aria-label={t('pdf.toolbar.pageLink')}>
          <button
            type="button"
            className="button ghost pdf-page-nav-button"
            disabled={!document || page <= 1}
            aria-label={t('pdf.toolbar.previousPage')}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
          >
            ←
          </button>
          <strong className="pdf-page-count">
            {document ? `${page} / ${document.numPages}` : '— / —'}
          </strong>
          <button
            type="button"
            className="button ghost pdf-page-nav-button"
            disabled={!document || page >= document.numPages}
            aria-label={t('pdf.toolbar.nextPage')}
            onClick={() => setPage((value) => document ? Math.min(document.numPages, value + 1) : value)}
          >
            →
          </button>
          <button
            type="button"
            className={`button pdf-link-current-page ${activeBookmark != null ? 'secondary' : 'primary'}`}
            disabled={!document || !activeSegno}
            onClick={() => void (activeBookmark != null ? removeActiveBookmark() : saveActiveBookmark())}
          >
            {activeBookmark != null ? t('pdf.toolbar.unlinkCurrent') : t('pdf.toolbar.linkCurrent')}
          </button>
        </div>
      </div>

      <div className="pdf-canvas-wrap" ref={wrapRef} onClick={tapNavigate}>
        {document
          ? <canvas ref={canvasRef} />
          : <div className="empty-state">{t('pdf.empty')}</div>}
      </div>
    </section>
  )
})
